import React, { useEffect, useState } from 'react';
import { downloadUrl, cmsSubsectionOfTopic } from './api.js';
import { Button, inputCls } from './ui.jsx';

/**
 * Export to Excel, three ways (operator order 2026-10-22): the whole year,
 * everything for one department or one person, or one CMS section (narrowed
 * to a subsection when wanted). The workbook follows the plan's format with
 * the logo. "One sheet per department" splits the chosen scope by department.
 */
export default function ExportModal({ boot, people, open, onClose, current = {} }) {
  const [scope, setScope] = useState('all');
  const [department, setDepartment] = useState('');
  const [person, setPerson] = useState('');
  const [section, setSection] = useState('');
  const [topic, setTopic] = useState('');
  const [split, setSplit] = useState(false);
  useEffect(() => {
    if (!open) return;
    // Start from what the list is showing.
    if (current.department) { setScope('department'); setDepartment(current.department); }
    else if (current.owner && current.owner !== 'none') { setScope('person'); setPerson(current.owner); }
    else if (current.section) { setScope('section'); setSection(current.section); setTopic(current.topic || ''); }
    else setScope('all');
  }, [open, current.department, current.owner, current.section, current.topic]);
  if (!open) return null;
  const cycleId = boot.cycle?.id;
  const sections = boot.edition?.sections || [];
  const topics = (boot.edition?.topics || []).filter((t) => t.section_letter === section);
  const ready = scope === 'all' || (scope === 'department' && department) || (scope === 'person' && person) || (scope === 'section' && section);
  const href = () => {
    const qs = new URLSearchParams();
    if (scope === 'department') qs.set('department', department);
    if (scope === 'person') qs.set('owner', person);
    if (scope === 'section') { qs.set('section', section); if (topic) qs.set('topic', topic); }
    if (split) qs.set('split', 'department');
    return downloadUrl(`/cycles/${cycleId}/export.xlsx${qs.toString() ? `?${qs}` : ''}`);
  };
  const Opt = ({ v, label, hint, children }) => (
    <label className={`block rounded-xl border px-3 py-2.5 cursor-pointer ${scope === v ? 'border-orange-300 bg-orange-50/40' : 'border-slate-200 hover:border-slate-300'}`}>
      <span className="flex items-center gap-2"><input type="radio" name="rd-export-scope" checked={scope === v} onChange={() => setScope(v)} className="accent-orange-600" /><span className="text-sm font-semibold text-slate-800">{label}</span><span className="text-xs text-slate-500">{hint}</span></span>
      {scope === v && children ? <div className="mt-2 pl-6">{children}</div> : null}
    </label>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4" onClick={onClose} role="dialog" aria-modal="true" aria-label="Export to Excel">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-200 p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div><h3 className="text-base font-bold text-slate-900">Export to Excel</h3><p className="text-xs text-slate-500 mt-0.5">In the plan's format with the logo. One row per CMS item, CMS's words verbatim.</p></div>
          <button type="button" className="text-slate-400 hover:text-slate-700 text-xl leading-none" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="mt-4 space-y-2">
          <Opt v="all" label="The whole year" hint={`every item of CY ${boot.cycle?.contract_year || ''}`} />
          <Opt v="department" label="One department" hint="everything that department is on">
            <select className={inputCls} value={department} onChange={(e) => setDepartment(e.target.value)}>
              <option value="">Choose a department</option>
              {(people?.departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Opt>
          <Opt v="person" label="One person" hint="everything that person is on">
            <select className={inputCls} value={person} onChange={(e) => setPerson(e.target.value)}>
              <option value="">Choose a person</option>
              {(people?.users || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Opt>
          <Opt v="section" label="One CMS section" hint="or one subsection in it">
            <div className="space-y-2">
              <select className={inputCls} value={section} onChange={(e) => { setSection(e.target.value); setTopic(''); }}>
                <option value="">Choose a section</option>
                {sections.map((s) => <option key={s.letter} value={s.letter}>{s.letter}. {s.title}</option>)}
              </select>
              {section && (
                <select className={inputCls} value={topic} onChange={(e) => setTopic(e.target.value)}>
                  <option value="">The whole section</option>
                  {topics.map((t) => <option key={t.key} value={t.key}>{cmsSubsectionOfTopic(t)}</option>)}
                </select>
              )}
            </div>
          </Opt>
        </div>
        {boot.is_compliance && scope !== 'department' && scope !== 'person' && (
          <label className="mt-3 flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" className="accent-orange-600" checked={split} onChange={(e) => setSplit(e.target.checked)} /> One sheet per department, with a summary sheet</label>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button kind="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!ready || !cycleId} onClick={() => { window.location.assign(href()); onClose(); }}>Download</Button>
        </div>
      </div>
    </div>
  );
}
